/* eslint-disable no-restricted-globals */

import { CHECK_EMAIL_REMINDER_PARAM } from "src/components/collapsible-header/CollapsibleHeader";
import { updateForwardedParams } from "src/utils/urlParams";
import {
  isAddonStoreEligible,
  isNoomSummitEligible,
  isUpsEx360MedActive,
  isUpsEx363MedBeforeBiomarkersActive,
  isUpsEx363MedReplaceBiomarkersActive,
  isUpsEx364MedBeforeMetabolicActive,
  isUpsEx366NadBeforeMetabolicActive,
} from "src/utils/userSegment/features";
import {
  getNextPostSignupCrossSellAd,
  PostSignupCrossSellAd,
  PostSignupCrossSellAdId,
} from "src/utils/addons/postSignupCrossSellRanking";
import { isInApp } from "src/utils/userSegment/in-app";
import { SessionStorageKey } from "src/utils/constants";
import { setNoomSessionStorage } from "src/utils/noomSessionStorage";
import {
  getSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";
import goto from ".";

export type OffersParams = {
  userId?: string;
  acceptedCoursePackOffer?: boolean;
};

export type CollapsibleHeaderUrlParams = {
  showEmailReminder?: "noomPremium" | "noomSummit" | "snackDelivery" | false;
};

function setTelehealthCrossSellSession() {
  setNoomSessionStorage(SessionStorageKey.isNewPatient, true);
  setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
}

export default {
  noomPremium() {
    goto.layer("noom-premium");
  },
  inAppNoomPremium() {
    goto.layer("noom-premium-in-app-upgrade");
  },
  addOns({ showEmailReminder }: CollapsibleHeaderUrlParams) {
    updateForwardedParams({
      [CHECK_EMAIL_REMINDER_PARAM]: showEmailReminder ?? null,
    });
    goto.layer("addons");
  },

  probioticsDelivery() {
    goto.layer("probiotics-delivery");
  },

  hrtSurvey() {
    goto.layer("hrt-survey");
  },

  fastForwardTrial({ showEmailReminder }: CollapsibleHeaderUrlParams) {
    updateForwardedParams({
      [CHECK_EMAIL_REMINDER_PARAM]: showEmailReminder ?? null,
    });
    goto.layer(`fast-forward`);
  },

  postSignupCrossSellExit() {
    updateSessionState("browser", { postSignupCrossSellQueue: [] });

    if (
      isUpsEx360MedActive() ||
      isUpsEx363MedBeforeBiomarkersActive() ||
      isUpsEx363MedReplaceBiomarkersActive() ||
      isUpsEx364MedBeforeMetabolicActive() ||
      isUpsEx366NadBeforeMetabolicActive()
    ) {
      goto.telehealthEnrollmentSetup();
      return;
    }

    if (isNoomSummitEligible()) {
      goto.layer("noom-summit");
      return;
    }
    if (isAddonStoreEligible()) {
      goto.addOns({ showEmailReminder: false });
      return;
    }
    if (isInApp()) {
      goto.nativePurchaseComplete();
      return;
    }
    goto.enrollment({});
  },

  startPostSignupCrossSell(queue: PostSignupCrossSellAd[]) {
    updateSessionState("browser", {
      postSignupCrossSellQueue: queue,
    });
    const [firstAd] = queue;
    if (firstAd) {
      goto.postSignupCrossSell(firstAd);
    }
  },

  postSignupCrossSell(ad: PostSignupCrossSellAd) {
    setTelehealthCrossSellSession();
    goto.layer(ad.layer);
  },

  advancePostSignupCrossSell(currentAdId: PostSignupCrossSellAdId) {
    const storedQueue =
      getSessionState("browser").postSignupCrossSellQueue ?? [];
    const nextAd = getNextPostSignupCrossSellAd(currentAdId, storedQueue);
    if (nextAd) {
      goto.postSignupCrossSell(nextAd);
    } else {
      goto.postSignupCrossSellExit();
    }
  },

  postNadCrossSellExit() {
    goto.advancePostSignupCrossSell(PostSignupCrossSellAdId.NAD);
  },

  postSermorelinCrossSellExit() {
    goto.advancePostSignupCrossSell(PostSignupCrossSellAdId.SERMORELIN);
  },

  postMetabolicCrossSellExit() {
    goto.advancePostSignupCrossSell(PostSignupCrossSellAdId.METABOLIC);
  },

  /**
   * Where a prescription shop product goes once bought, rather than closing the
   * webview like the OTC flows do. Sets the telehealth flag for the standalone
   * flow, which has nothing setting it upstream.
   */
  shopIntake() {
    setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
    goto.telehealthEnrollmentSetup();
  },
};
